<script setup>
import { useRouter, useRoute } from 'vue-router'
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useServiceStore } from '@stores/service'
import { register } from 'swiper/element/bundle'
import { EffectCards } from 'swiper/modules'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import ServiceFrame from '@components/global/accents/ServiceFrame.vue'
import ServiceCard from '@components/services/ServiceCard.vue'
import Button from '@components/global/forms/Button.vue'

import 'swiper/css'
import 'swiper/css/effect-cards'

register()
gsap.registerPlugin(ScrollTrigger)

const router = useRouter()
const route = useRoute()

const serviceStore = useServiceStore()
const serviceSlug = computed(() => route.params.hash)

const swiper = ref()
const currentService = ref(0)
const currentServiceSlug = ref(null)

const pointerPosition = ref(0)
const serviceCards = ref([])
const scrollTriggers = ref([])

const slideTo = (index) => {
	if (swiper.value?.swiper) {
		swiper.value.swiper.slideTo(index)
	}
}

const updatePointer = async (slug) => {
	await nextTick()

	// return new Promise((resolve) => {
	// 	setTimeout(() => {
	const cardEl = serviceCards.value[slug]?.$el
	pointerPosition.value = cardEl ? cardEl.offsetTop : 0
	resolve()
	// 	}, 200)
	// })
}

function selectService(index) {
	currentService.value = index
	const slug = serviceStore.categories?.[index]?.slug
	if (!slug) return

	currentServiceSlug.value = slug
	slideTo(index)
	// if (route.hash.substring(1) !== slug) {
	// 	router.push({ hash: `#${slug}` })
	// }
	updatePointer(slug)
}

const setupScrollTriggers = () => {
	scrollTriggers.value.forEach((trigger) => trigger.kill())
	scrollTriggers.value = []

	nextTick(() => {
		serviceStore.categories.forEach((service, index) => {
			const cardEl = serviceCards.value[service.slug]?.$el
			if (!cardEl) return

			const trigger = ScrollTrigger.create({
				trigger: cardEl,
				start: 'top center',
				end: 'bottom center',
				onEnter: () => selectService(index),
				onEnterBack: () => selectService(index),
			})

			scrollTriggers.value.push(trigger)
		})
	})
}

onMounted(() => {
	slideTo(currentService.value)
	swiper.value.swiper.on('slideChange', () => {
		selectService(swiper.value.swiper.activeIndex)
	})
	setupScrollTriggers()
})

onUnmounted(() => {
	scrollTriggers.value.forEach((trigger) => trigger.kill())
})

watch(
	[() => route.hash, () => serviceStore.categories],
	([hash, categories]) => {
		if (!categories?.length) return

		const slug = hash ? hash.substring(1) : categories[0].slug
		const index = categories.findIndex((service) => service.slug === slug)
		if (index === -1) return

		currentService.value = index
		currentServiceSlug.value = slug

		slideTo(index)
		updatePointer(slug)

		setupScrollTriggers()
	},
	{ immediate: true },
)
</script>

<template>
	<section id="services" class="relative bg-crowns-dark-grey py-10 lg:py-16">
		<div class="container">
			<div class="grid lg:grid-cols-2">
				<div class="sticky top-20 hidden self-start p-24 lg:block">
					<swiper-container ref="swiper" class="w-full xl:pr-16 2xl:pr-32" :modules="[EffectCards]" effect="cards" :grabCursor="true" @swiper="setSwiper">
						<swiper-slide v-for="(service, i) in serviceStore.categories" class="overflow-visible">
							<ServiceFrame :service="service" orientation="horizontal" :isActive="currentService === i" />
						</swiper-slide>
					</swiper-container>
				</div>
				<div class="relative flex flex-col gap-16 md:pl-28">
					<img class="absolute left-0 hidden w-20 pt-1 transition-all duration-200 ease-in-out md:block xl:w-24" :style="{ top: `${pointerPosition}px` }" src="https://cdn.papercrowns.com/website/images/elements/etches/finger.png" alt="Finger Pointing Right" />
					<ServiceCard v-for="(service, i) in serviceStore.categories" :key="i" :ref="(el) => (serviceCards[service.slug] = el)" :service="service" @click="selectService(i)" :expanded="true" />
				</div>
			</div>
		</div>
	</section>
</template>
